import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Users,
  Calendar,
  Clock,
  Mic2,
  Video,
  MessageSquare,
  Sparkles,
  ArrowRight,
} from "lucide-react";

import { Heading } from "~/components/ui/heading";
import {
  getSummitByIdFn,
  summitRecapStatsQueryOptions,
  summitRecapStoriesQueryOptions,
  type SummitRecapStats,
} from "~/lib/functions/transformation-story";
import { createBrandMeta } from "~/lib/shared/meta";
import { StoryCard } from "~/routes/_public/stories/-components/story-card";

/**
 * Summit recap page showing stats and transformation stories.
 * Route: /summit/:summitId/recap
 */

export const Route = createFileRoute("/_public/summit/$summitId/recap")({
  loader: async ({ context, params }) => {
    const summit = await getSummitByIdFn({
      data: { summitId: params.summitId },
    });

    if (!summit) {
      throw new Error("Summit not found");
    }

    await Promise.all([
      context.queryClient.ensureQueryData(
        summitRecapStatsQueryOptions(params.summitId),
      ),
      context.queryClient.ensureQueryData(
        summitRecapStoriesQueryOptions(params.summitId),
      ),
    ]);

    return { summit };
  },
  head: ({ loaderData }) => {
    const summitTitle = loaderData?.summit?.title ?? "Summit";
    return createBrandMeta({
      title: `${summitTitle} Recap`,
      description: `Relive the magic of ${summitTitle}. See the impact: coaching hours delivered, lives transformed, and stories of real change from our community.`,
      ogType: "website",
    });
  },
  component: SummitRecapPage,
});

function SummitRecapPage() {
  const { summit } = Route.useLoaderData();
  const params = Route.useParams();

  const { data: stats } = useSuspenseQuery(
    summitRecapStatsQueryOptions(params.summitId),
  );
  const { data: stories } = useSuspenseQuery(
    summitRecapStoriesQueryOptions(params.summitId),
  );

  return (
    <div className="relative min-h-dvh overflow-hidden bg-gradient-to-b from-gray-50 to-white">
      {/* Decorative background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-20 -left-40 h-[600px] w-[600px] rounded-full bg-gradient-to-br from-purple-100/50 via-indigo-50/40 to-transparent blur-3xl" />
        <div className="absolute top-[30%] -right-40 h-[500px] w-[500px] rounded-full bg-gradient-to-bl from-amber-100/40 via-orange-50/30 to-transparent blur-3xl" />
        <div className="absolute bottom-20 -left-20 h-[400px] w-[400px] rounded-full bg-gradient-to-tr from-teal-100/30 via-emerald-50/20 to-transparent blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Header */}
        <Heading
          preHeadline="Thank You for Being Part of"
          headline={summit.title}
          subheadline="Together, we created something special. Here's a look at the impact we made and the lives transformed."
          isH1
        />

        {/* Stats Section */}
        <div className="mt-16">
          <StatsGrid stats={stats} />
        </div>

        {/* Session Format Breakdown */}
        {stats.sessionsByFormat.length > 0 && (
          <div className="mt-16">
            <h2 className="mb-8 text-center text-2xl font-semibold text-gray-900">
              Summit Content
            </h2>
            <SessionFormatGrid formats={stats.sessionsByFormat} />
          </div>
        )}

        {/* Transformation Stories Section */}
        {stories.length > 0 && (
          <div className="mt-20">
            <div className="mb-12 text-center">
              <p className="mb-2 text-sm font-medium tracking-wide text-purple-700 uppercase">
                Real Impact
              </p>
              <h2 className="text-3xl font-semibold text-gray-900">
                Transformation Stories
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-lg text-gray-600">
                Hear directly from participants whose lives were changed during
                the summit.
              </p>
            </div>

            {/* Stories Grid */}
            <div className="columns-1 gap-6 sm:columns-2 lg:columns-3">
              {stories.map((story, index) => (
                <StoryCard key={story.id} story={story} index={index} />
              ))}
            </div>

            {/* Link to all stories */}
            <div className="mt-12 text-center">
              <Link
                to="/stories"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-3 text-sm font-medium text-white shadow-md transition hover:from-purple-700 hover:to-indigo-700 hover:shadow-lg"
              >
                View All Stories
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-20">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 via-indigo-600 to-purple-700 px-8 py-12 text-center shadow-xl sm:px-12 sm:py-16">
            <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10" />
            <div className="relative">
              <Sparkles className="mx-auto mb-4 h-10 w-10 text-amber-300" />
              <h2 className="text-2xl font-bold text-white sm:text-3xl">
                Don't Miss the Next One
              </h2>
              <p className="mx-auto mt-4 max-w-lg text-lg text-purple-100">
                Join our community and be the first to know about upcoming
                summits and exclusive coaching opportunities.
              </p>
              <div className="mt-8">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3 text-sm font-semibold text-purple-700 shadow-md transition hover:bg-purple-50 hover:shadow-lg"
                >
                  Find Your Coach
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const compactFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

function formatCompact(value: number): string {
  return compactFormatter.format(value);
}

function StatsGrid({ stats }: { stats: SummitRecapStats }) {
  const statItems = [
    {
      label: "Registered Attendees",
      value: formatCompact(stats.totalRegistrations),
      icon: Users,
      color: "from-purple-500 to-indigo-500",
      bgColor: "bg-purple-50",
    },
    {
      label: "Sessions Delivered",
      value: formatCompact(stats.totalSessions),
      icon: Calendar,
      color: "from-amber-500 to-orange-500",
      bgColor: "bg-amber-50",
    },
    {
      label: "Coaching Hours Delivered",
      value: formatCompact(stats.totalCoachingHoursDelivered),
      icon: Clock,
      color: "from-teal-500 to-emerald-500",
      bgColor: "bg-teal-50",
    },
    {
      label: "Expert Speakers",
      value: formatCompact(stats.totalSpeakers),
      icon: Mic2,
      color: "from-rose-500 to-pink-500",
      bgColor: "bg-rose-50",
    },
  ];

  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {statItems.map((stat) => (
        <div
          key={stat.label}
          className="group relative overflow-hidden rounded-2xl border border-white/60 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg"
        >
          <div
            className={`absolute -top-4 -right-4 h-24 w-24 rounded-full ${stat.bgColor} opacity-50 transition-transform duration-300 group-hover:scale-125`}
          />
          <div className="relative">
            <div
              className={`mb-4 inline-flex rounded-xl bg-gradient-to-br ${stat.color} p-3 text-white shadow-md`}
            >
              <stat.icon className="h-6 w-6" />
            </div>
            <div className="text-4xl font-bold text-gray-900">{stat.value}</div>
            <div className="mt-1 text-sm font-medium text-gray-500">
              {stat.label}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const formatIcons: Record<string, typeof Video> = {
  presentation: Video,
  workshop: Users,
  panel: MessageSquare,
  "q&a": MessageSquare,
  keynote: Mic2,
  recording: Video,
};

const formatColors: Record<string, string> = {
  presentation: "from-blue-500 to-indigo-500 bg-blue-50",
  workshop: "from-purple-500 to-violet-500 bg-purple-50",
  panel: "from-amber-500 to-orange-500 bg-amber-50",
  "q&a": "from-teal-500 to-emerald-500 bg-teal-50",
  keynote: "from-rose-500 to-pink-500 bg-rose-50",
  recording: "from-gray-500 to-gray-500 bg-gray-50",
};

const formatLabels: Record<string, string> = {
  presentation: "Presentations",
  workshop: "Workshops",
  panel: "Panel Discussions",
  "q&a": "Q&A Sessions",
  keynote: "Keynotes",
  recording: "Recordings",
};

function SessionFormatGrid({
  formats,
}: {
  formats: Array<{ format: string; count: number }>;
}) {
  return (
    <div className="flex flex-wrap justify-center gap-4">
      {formats.map(({ format, count }) => {
        const Icon = formatIcons[format] ?? Video;
        const colors =
          formatColors[format] ?? "from-gray-500 to-gray-500 bg-gray-50";
        const [gradientColors, bgColor] = colors.split(" ");
        const label = formatLabels[format] ?? format;

        return (
          <div
            key={format}
            className={`flex items-center gap-3 rounded-full ${bgColor} px-5 py-3 shadow-sm`}
          >
            <div
              className={`inline-flex rounded-lg bg-gradient-to-br ${gradientColors} p-2 text-white`}
            >
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <span className="text-lg font-bold text-gray-900">{count}</span>
              <span className="ml-1 text-sm text-gray-600">{label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
